import { useEffect, useMemo, useState } from 'react'
import { getActiveBundleListAsync } from 'src/redux/actions/activeBundleAction'
import { getPlatformProductVarientDetailAsync } from 'src/redux/actions/productActions'
import { useAppDispatch } from 'src/redux/storeHooks'
import {
  extractBundleRelationDataFromProductDetail,
  getOrderedParentDetails,
  OrderedParentDetailRow,
  ProductLinkingContext,
} from './productLinkingDetailUtils'

type UseProductLinkingDetailsArgs = {
  platformProductDetail: any
  selectedVariant: any
  variantId?: string
  platformId?: string
  mainStoreId?: number
  fetchedConnectedPlatformData: any[]
}

export const useProductLinkingDetails = ({
  platformProductDetail,
  selectedVariant,
  variantId,
  platformId,
  mainStoreId,
  fetchedConnectedPlatformData,
}: UseProductLinkingDetailsArgs) => {
  const dispatch = useAppDispatch()
  const [resolvedBundleRelationData, setResolvedBundleRelationData] = useState<any[]>([])

  const productVariantDetail = useMemo(() => {
    const variants = platformProductDetail?.varients_data
    if (!Array.isArray(variants)) return undefined
    return variants.find((item: any) => Number(item?.id) === Number(variantId ?? selectedVariant?.id))
  }, [platformProductDetail?.varients_data, variantId, selectedVariant?.id])

  useEffect(() => {
    let cancelled = false

    const resolveBundleRelationsFromLinkedChannel = async () => {
      const isMainStoreView = mainStoreId && Number(platformId) === Number(mainStoreId)

      if (!isMainStoreView || !selectedVariant?.sku) {
        setResolvedBundleRelationData([])
        return
      }

      const existingBundleRelationData =
        extractBundleRelationDataFromProductDetail(platformProductDetail)
      const selectedVariantBundleData = Array.isArray(selectedVariant?.bundle_relation_data)
        ? selectedVariant.bundle_relation_data
        : []

      if (existingBundleRelationData.length > 0 || selectedVariantBundleData.length > 0) {
        setResolvedBundleRelationData([])
        return
      }

      const searchSku = `${selectedVariant.sku}`.trim()
      if (!searchSku) {
        setResolvedBundleRelationData([])
        return
      }

      const platformsToSearch = (fetchedConnectedPlatformData || []).filter(
        (platform: any) => Number(platform?.id) !== Number(mainStoreId)
      )

      for (const platform of platformsToSearch) {
        try {
          const bundleResponse: any = await dispatch(
            getActiveBundleListAsync({
              paramData: {
                platform_id: Number(platform.id),
                page: 1,
                limit: 10,
                search: searchSku,
              },
            })
          )

          if (cancelled) return

          const bundles = bundleResponse?.payload?.data?.data ?? []
          const matchedBundle = bundles.find(
            (bundle: any) =>
              `${bundle?.sku ?? ''}`.trim().toLowerCase() === searchSku.toLowerCase()
          )

          if (!matchedBundle?.platform_product_variant_id) continue

          const variantResponse: any = await dispatch(
            getPlatformProductVarientDetailAsync({
              productVarientId: String(matchedBundle.platform_product_variant_id),
            })
          )

          if (cancelled) return

          const linkedVariant = variantResponse?.payload?.varients_data?.find(
            (variant: any) =>
              Number(variant?.id) === Number(matchedBundle.platform_product_variant_id)
          )
          const bundleRelationData = linkedVariant?.bundle_relation_data

          if (Array.isArray(bundleRelationData) && bundleRelationData.length > 0) {
            setResolvedBundleRelationData(bundleRelationData)
            return
          }
        } catch {
          // Try the next connected platform.
        }
      }

      if (!cancelled) {
        setResolvedBundleRelationData([])
      }
    }

    resolveBundleRelationsFromLinkedChannel()

    return () => {
      cancelled = true
    }
  }, [
    selectedVariant?.sku,
    selectedVariant?.bundle_relation_data,
    platformId,
    platformProductDetail,
    mainStoreId,
    fetchedConnectedPlatformData,
    dispatch,
  ])

  const linkingContext: ProductLinkingContext = useMemo(
    () => ({
      platformProductDetail,
      selectedVariant,
      variantId,
      resolvedBundleRelationData,
    }),
    [platformProductDetail, selectedVariant, variantId, resolvedBundleRelationData]
  )

  const linkingRows: OrderedParentDetailRow[] = useMemo(
    () =>
      getOrderedParentDetails({
        productVariantDetail,
        platformProductDetail,
        selectedVariant,
        variantId,
        platformId,
        mainStoreId,
        resolvedBundleRelationData,
      }),
    [
      productVariantDetail,
      platformProductDetail,
      selectedVariant,
      variantId,
      platformId,
      mainStoreId,
      resolvedBundleRelationData,
    ]
  )

  return {
    linkingRows,
    linkingContext,
    productVariantDetail,
    resolvedBundleRelationData,
  }
}
